import {
  AspectRatio,
  Center,
  ComponentWithAs,
  Flex,
  Hide,
  HStack,
  IconProps,
  Skeleton,
  StackDivider,
  Text,
  VStack,
} from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import { tightMapCalc } from '../../utils/colorMaps/tightMap'
import { periodEnum, periodGuard } from '../building/buildingConfiguration'

type pfHeadlineItemProps = {
  Icon: ComponentWithAs<'svg', IconProps>
  label: string
  value: number
  target: number
  units: string
  description: string
  upIsBetter: boolean
  period?: string
  loading: boolean
}

const PfHeadlineItem = ({
  Icon,
  label,
  value,
  target,
  units,
  description,
  upIsBetter,
  period,
  loading,
}: pfHeadlineItemProps) => {
  const { t } = useTranslation(['portfolio'])
  const percentage = loading ? 100 : Math.round((value / target) * 100)
  const { color: iconColor } = tightMapCalc({
    value: percentage,
    range: 150,
    upIsBetter,
  })
  const iconSize = {
    base: 10,
    sm: 10,
    md: 8,
    lg: 8,
    xl: 10,
    '2xl': 10,
  }
  const headingFontSize = {
    base: '0.8em',
    sm: '0.9em',
    md: '0.9em',
    lg: '0.95em',
    xl: '1.05em',
    '2xl': '1.1em',
  }
  const spacing = {
    base: '2px',
    sm: '2px',
    md: '2px',
    lg: '0px',
    xl: '3px',
    '2xl': '4px',
  }
  const numberFontSize = {
    base: '1em',
    sm: '1.1em',
    md: '1.3em',
    lg: '1.4em',
    xl: '1.65em',
    '2xl': '1.75em',
  }
  const lowerSpacing = {
    base: '0px',
    sm: '0px',
    md: '0px',
    lg: '0px',
    xl: '0px',
    '2xl': '1px',
  }
  const textFontSize = {
    base: '0.8em',
    sm: '0.8em',
    md: '0.8em',
    lg: '0.9em',
    xl: '0.8em',
    '2xl': '0.9em',
  }

  let periodText: string = 'lastMonth'
  if (periodGuard(period)) {
    if (period === periodEnum.hourly) {
      periodText = 'lastHour'
    } else if (period === periodEnum.daily) {
      periodText = 'lastDay'
    } else if (period === periodEnum.weekly) {
      periodText = 'lastWeek'
    } else if (period === periodEnum.monthly) {
      periodText = 'lastMonth'
    } else if (period === periodEnum.yearly) {
      periodText = 'lastYear'
    }
  }

  return (
    <AspectRatio
      ratio={1}
      backgroundColor={'white'}
      w={'12.5em'}
      shadow="md"
      textColor={'purple.500'}
    >
      <Center p={2}>
        <VStack
          divider={
            <StackDivider borderColor={iconColor} w="70%" alignSelf="center" />
          }
          height="100%"
          width="100%"
          spacing={spacing}
          padding="0"
        >
          <VStack
            height="50%"
            width="100%"
            spacing={spacing}
            justifyContent="space-around"
          >
            <Hide breakpoint="(max-width: 330px)">
              <Skeleton isLoaded={!loading}>
                <Center>
                  <Icon
                    minWidth={iconSize}
                    h={iconSize}
                    color={iconColor}
                    display="block"
                  />
                </Center>
              </Skeleton>
            </Hide>
            <Skeleton isLoaded={!loading}>
              <Text
                fontSize={headingFontSize}
                textAlign="center"
                lineHeight="1.2em"
              >
                {t(label).toLocaleUpperCase()}
              </Text>
            </Skeleton>
          </VStack>
          <VStack
            height="50%"
            width="100%"
            overflow="hidden"
            spacing={lowerSpacing}
            padding="4px"
            justifyContent="space-around"
          >
            <Skeleton isLoaded={!loading} as={HStack}>
              <Flex alignItems={'baseline'}>
                <Text fontSize={numberFontSize}>
                  {value.toLocaleString('en', { maximumSignificantDigits: 3 })}
                </Text>

                <Text
                  dangerouslySetInnerHTML={{ __html: '&thinsp;' + units }}
                ></Text>
              </Flex>
            </Skeleton>
            <VStack spacing="0">
              <Center maxW="100%">
                <Hide below="xl">
                  <Skeleton isLoaded={!loading}>
                    <Text textAlign={'center'} fontSize={textFontSize}>
                      {t(description).toLocaleUpperCase()}
                    </Text>
                  </Skeleton>
                </Hide>
              </Center>
              <Center>
                <Hide breakpoint="(max-width: 300px)">
                  <Skeleton isLoaded={!loading}>
                    <Text textAlign={'center'} fontSize={textFontSize}>
                      {t(periodText).toLocaleUpperCase()}
                    </Text>
                  </Skeleton>
                </Hide>
              </Center>
            </VStack>
          </VStack>
        </VStack>
      </Center>
    </AspectRatio>
  )
}

export default PfHeadlineItem
